import { useEffect, useState } from "react";
import PageHero from "../components/PageHero";
import ProductCard from "../components/ProductCard";
import QuoteDialog from "../components/QuoteDialog";
import { fetchCategories, fetchProducts } from "../lib/api";

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, product: null });

  useEffect(() => {
    (async () => {
      try {
        const [p, c] = await Promise.all([fetchProducts(), fetchCategories()]);
        setProducts(p);
        setCategories(["All", ...c]);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = active === "All" ? products : products.filter((p) => p.category === active);

  const onQuote = (product) => setDialog({ open: true, product });

  return (
    <>
      <PageHero
        eyebrow="Catalogue"
        title="Ingredients that make formulations sing."
        description="A curated selection across every major cosmetic and BPC raw material category. Filter, explore, and request a quote — our team will respond with availability, pricing and documentation."
        image="https://images.pexels.com/photos/6621462/pexels-photo-6621462.jpeg?auto=compress&cs=tinysrgb&w=2400"
      />

      <section className="section-y">
        <div className="container-x">
          <div className="flex flex-wrap gap-2 mb-12" data-testid="category-filter">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`filter-${c.replace(/[\s&]+/g, "-").toLowerCase()}`}
                className={`px-5 py-2.5 text-sm tracking-wide border transition-colors ${
                  active === c
                    ? "bg-brand-moss border-brand-moss text-white"
                    : "bg-brand-surface border-brand-line text-brand-ink/80 hover:border-brand-moss"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-brand-surfaceAlt/50 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-brand-muted py-20">No products in this category yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-grid">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} onQuote={onQuote} />
              ))}
            </div>
          )}

          <div className="mt-20 p-10 md:p-14 bg-brand-surfaceAlt text-center">
            <p className="eyebrow mb-3">Can't find what you need?</p>
            <h3 className="font-serif text-3xl md:text-4xl max-w-2xl mx-auto leading-tight">
              Our catalogue shows a glimpse. Tell us what you're looking for — we likely source it.
            </h3>
            <a href="/contact" className="btn-primary mt-8" data-testid="products-cta-contact">
              Talk to our sourcing team
            </a>
          </div>
        </div>
      </section>

      <QuoteDialog
        product={dialog.product}
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
      />
    </>
  );
}
